"use client";

import { Tool } from "@/types";
import Link from "next/link";
import { getFAQs, getRelatedTools, getToolOverview, getToolUseCases } from "@/lib/seo";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname, withLocale } from "@/lib/i18n";
import { getSiteContent } from "@/lib/site-content";
import { getLocalizedTool, getLocalizedTools } from "@/lib/tool-localization";
import { getToolSeoSteps } from "@/lib/tool-seo-copy";

interface ToolSEOContentProps {
  tool: Tool;
}

export function ToolSEOContent({ tool }: ToolSEOContentProps) {
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const localizedTool = getLocalizedTool(tool, locale);
  const content = getSiteContent(locale).toolsPage;
  const faqs = getFAQs(localizedTool, locale);
  const relatedTools = getLocalizedTools(getRelatedTools(tool), locale);
  const useCases = getToolUseCases(localizedTool, locale);
  const overview = getToolOverview(localizedTool, locale);
  const steps = getToolSeoSteps(locale);

  return (
    <div className="mt-20 max-w-4xl mx-auto space-y-16">
      <section>
        <h2 className="text-3xl font-bold mb-6">{content.whatMakesUseful}</h2>
        <div className="rounded-[30px] border border-black/10 bg-positivus-grey p-8">
          <p className="text-lg leading-8 text-gray-700">{overview}</p>
        </div>
      </section>
      
      {/* How to Section */}
      <section>
        <h2 className="text-3xl font-bold mb-6">{content.howToOnline}</h2>
        <div className="bg-white rounded-[30px] p-8 border border-black/10">
          <ol className="space-y-4 list-decimal list-inside text-lg text-gray-700">
            <li>
              <span className="font-medium text-black">{content.uploadOrEnter}:</span> {steps.upload(localizedTool.name)}
            </li>
            <li>
              <span className="font-medium text-black">{content.configure}:</span> {steps.configure}
            </li>
            <li>
              <span className="font-medium text-black">{content.processDownload}:</span> {steps.process}
            </li>
          </ol>
        </div>
      </section>

      <section>
        <h2 className="text-3xl font-bold mb-6">{content.bestUseCases}</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {useCases.map((useCase) => (
            <div key={useCase} className="rounded-[24px] border border-black/10 bg-white p-6">
              <p className="text-base leading-7 text-gray-700">{useCase}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section>
        <h2 className="text-3xl font-bold mb-6">{content.whyChoose}</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-positivus-grey p-6 rounded-[20px] border border-black/5">
            <h3 className="text-xl font-bold mb-3">{content.fastFree}</h3>
            <p className="text-gray-600">{content.fastFreeBody}</p>
          </div>
          <div className="bg-positivus-grey p-6 rounded-[20px] border border-black/5">
            <h3 className="text-xl font-bold mb-3">{content.securePrivate}</h3>
            <p className="text-gray-600">{content.securePrivateBody}</p>
          </div>
          <div className="bg-positivus-grey p-6 rounded-[20px] border border-black/5">
            <h3 className="text-xl font-bold mb-3">{content.highQuality}</h3>
            <p className="text-gray-600">{content.highQualityBody}</p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section>
        <h2 className="text-3xl font-bold mb-6">{content.faqTitle}</h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-white rounded-[20px] border border-black/10 p-6">
              <h3 className="text-lg font-bold mb-2">{faq.question}</h3>
              <p className="text-gray-600">{faq.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {relatedTools.length > 0 && (
        <section>
          <h2 className="text-3xl font-bold mb-6">{content.relatedCategory}</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {relatedTools.map((relatedTool) => (
              <Link
                key={relatedTool.id}
                href={withLocale(locale, `/${relatedTool.slug}`)}
                className="rounded-[24px] border border-black/10 bg-white p-6 transition-colors hover:bg-positivus-green/20"
              >
                <h3 className="mb-2 text-xl font-bold">{relatedTool.name}</h3>
                <p className="text-sm leading-6 text-gray-600">{relatedTool.description}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
      
    </div>
  );
}
